import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TimeAndSales = ({ selectedContract }) => {
  const [tradesData, setTradesData] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'buy', 'sell'
  const [isAutoScroll, setIsAutoScroll] = useState(true);

  // Mock trades data
  const generateMockTrade = () => {
    const basePrice = selectedContract?.includes('EUA') ? 85.25 : 78.25;
    const priceVariation = (Math.random() - 0.5) * 2; // ±1 price variation
    const price = basePrice + priceVariation;
    const quantity = Math.floor(Math.random() * 2000) + 100;
    const side = Math.random() > 0.5 ? 'buy' : 'sell';
    
    return {
      id: Date.now() + Math.random(),
      time: new Date(),
      price: price,
      quantity: quantity,
      side: side,
      exchange: selectedContract?.includes('EUA') ? 'ICE' : 'NYMEX',
      isNew: true
    };
  };

  useEffect(() => {
    // Initialize with some historical trades
    const initialTrades = Array.from({ length: 20 }, (_, i) => ({
      ...generateMockTrade(),
      time: new Date(Date.now() - (20 - i) * 30000), // 30 seconds apart
      isNew: false
    }));
    
    setTradesData(initialTrades);

    // Add new trades periodically
    const interval = setInterval(() => {
      const newTrade = generateMockTrade();
      setTradesData(prev => {
        const updated = [newTrade, ...prev?.slice(0, 99)]; // Keep last 100 trades
        return updated?.map((trade, index) => ({
          ...trade,
          isNew: index === 0 ? true : false
        }));
      });

      // Remove new flag after animation
      setTimeout(() => {
        setTradesData(prev => prev?.map(trade => ({ ...trade, isNew: false })));
      }, 1000);
    }, Math.random() * 3000 + 1000); // Random interval between 1-4 seconds

    return () => clearInterval(interval);
  }, [selectedContract]);

  const formatPrice = (price) => {
    return selectedContract?.includes('EUA') ? `€${price?.toFixed(2)}` : `$${price?.toFixed(2)}`;
  };

  const formatQuantity = (quantity) => {
    return quantity?.toLocaleString();
  };

  const formatTime = (time) => {
    return time?.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const filteredTrades = tradesData?.filter(trade => {
    if (filter === 'all') return true;
    return trade?.side === filter;
  });

  const getTotalVolume = () => {
    return filteredTrades?.reduce((sum, trade) => sum + trade?.quantity, 0);
  };

  const getTradeCount = () => {
    return filteredTrades?.length;
  };

  const getAveragePrice = () => {
    if (filteredTrades?.length === 0) return 0;
    const totalValue = filteredTrades?.reduce((sum, trade) => sum + (trade?.price * trade?.quantity), 0);
    const totalQuantity = filteredTrades?.reduce((sum, trade) => sum + trade?.quantity, 0);
    return totalValue / totalQuantity;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Time & Sales</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsAutoScroll(!isAutoScroll)}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isAutoScroll 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              title={isAutoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
            >
              <Icon name={isAutoScroll ? 'Pause' : 'Play'} size={14} />
            </button>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex items-center gap-2 mb-4">
          {[
            { key: 'all', label: 'All', icon: 'List' },
            { key: 'buy', label: 'Buys', icon: 'TrendingUp' },
            { key: 'sell', label: 'Sells', icon: 'TrendingDown' }
          ]?.map(filterOption => (
            <button
              key={filterOption?.key}
              onClick={() => setFilter(filterOption?.key)}
              className={`
                flex items-center gap-2 px-3 py-1 text-xs rounded-md transition-colors duration-200
                ${filter === filterOption?.key 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }
              `}
            >
              <Icon name={filterOption?.icon} size={12} />
              <span>{filterOption?.label}</span>
            </button>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div className="text-center">
            <div className="text-muted-foreground">Total Volume</div>
            <div className="font-mono font-semibold text-foreground">
              {formatQuantity(getTotalVolume())}
            </div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground">Trades</div>
            <div className="font-mono font-semibold text-foreground">
              {getTradeCount()}
            </div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground">Avg Price</div>
            <div className="font-mono font-semibold text-foreground">
              {formatPrice(getAveragePrice())}
            </div>
          </div>
        </div>
      </div>
      {/* Trades List */}
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-muted/50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-left">Time</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Price</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Size</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-center">Side</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-center">Exchange</th>
            </tr>
          </thead>
          <tbody>
            {filteredTrades?.map((trade) => (
              <tr
                key={trade?.id}
                className={`
                  border-b border-border/50 hover:bg-muted/30 transition-all duration-200
                  ${trade?.isNew ? 'bg-accent/20 animate-pulse' : ''}
                `}
              >
                <td className="px-3 py-2 text-xs font-mono text-muted-foreground">
                  {formatTime(trade?.time)}
                </td>
                <td className={`px-3 py-2 text-xs font-mono font-semibold text-right ${
                  trade?.side === 'buy' ? 'text-success' : 'text-error'
                }`}>
                  {formatPrice(trade?.price)}
                </td>
                <td className="px-3 py-2 text-xs font-mono text-right text-foreground">
                  {formatQuantity(trade?.quantity)}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`
                    inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium
                    ${trade?.side === 'buy' ?'bg-success/20 text-success' :'bg-error/20 text-error'
                    }
                  `}>
                    <Icon 
                      name={trade?.side === 'buy' ? 'ArrowUp' : 'ArrowDown'} 
                      size={10} 
                    />
                    {trade?.side?.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-center text-muted-foreground">
                  {trade?.exchange}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTrades?.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            <Icon name="Activity" size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No trades found for the selected filter</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeAndSales;